import { Chip } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { AppointmentAssessment } from '@uplift/api';
import { useL10n } from '@uplift/hooks';
import { mapAssessmentScoreToDescription } from '@uplift/utils';
import { Fragment, useCallback } from 'react';
import _ from 'lodash';
import { grey } from '@mui/material/colors';
import { useGetFormKey } from './appointment-assessment-chip.hooks';
import { AssessmentKeys } from '@uplift/types';

export type AppointmentAssessmentChipProps = {
  appointmentAssessment: AppointmentAssessment;
};

const getSignData = (sign: number) => {
  switch (sign) {
    case 1:
      return { classKey: 'error', symbol: '+' };
    case -1:
      return { classKey: 'success', symbol: '-' };
    default:
      return { classKey: '', symbol: <>&plusmn;</> };
  }
};

export const AppointmentAssessmentChip: React.FC<AppointmentAssessmentChipProps> = ({ appointmentAssessment }) => {
  const { classes, cx } = useStyles();
  const l10n = useL10n();

  const renderLabel = useCallback((parts: (React.ReactNode | string | null)[]) => {
    return parts
      .filter(x => x)
      .map((part, index, { length }) => {
        const isLastPart = length === index + 1;

        return <Fragment key={index}>{part}{isLastPart ? '' : ' | '}</Fragment>;
      });
  }, []);

  const getScoreDiffPart = useCallback((scoreDiffPercentage: number | null) => {
    if (scoreDiffPercentage === null) return null;
    // 0, 1, -1
    const sign = scoreDiffPercentage === 0 ? 0 : Math.sign(scoreDiffPercentage);
    const signData = getSignData(sign);

    return (
      <span className={cx([ classes.fontWeight700, classes[signData.classKey as keyof typeof classes] ])}>
        {signData.symbol}{Math.abs(scoreDiffPercentage)}%
      </span>
    );
  }, [ classes ]);

  const { formTitle, submissionId, score, scoreDiffPercentage, formId } = appointmentAssessment;
  const scoreDiffPart = getScoreDiffPart(scoreDiffPercentage);

  const formKey = useGetFormKey(formId);
  const scoreDescription = formKey ? mapAssessmentScoreToDescription(formKey as AssessmentKeys, l10n)(score) : null;
  const scorePart = scoreDescription ? _.startCase(`${score} ${scoreDescription}`) : null;

  return (
    <Chip
      label={renderLabel([ formTitle, scorePart, scoreDiffPart ])}
      key={submissionId}
      className={classes.chip}
    />
  );
};

const useStyles = makeStyles()((theme) => ({
  chip: {
    borderRadius: 0,
    background: grey[200]
  },
  fontWeight700: {
    fontWeight: 700,
  },
  success: {
    color: theme.palette.success.main
  },
  error: {
    color: theme.palette.error.main
  }
}));
